import { useQuery } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  Check,
  Clock,
  Feather,
  Flame,
  Image as ImageIcon,
  Mountain,
  RotateCcw,
  Type,
  Upload,
  X,
  Zap,
} from 'lucide-react'
import { useRef } from 'react'
import { BrandLogo } from '../components/brand-logo'
import { SettingsLayout } from '../components/settings-layout'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { api } from '../lib/api'
import {
  BACKGROUNDS,
  type BackgroundId,
  FONT_FAMILIES,
  FONT_SCALES,
  type FontFamilyId,
  type FontScaleId,
  LOGO_PRESETS,
  type LogoId,
  useAppearance,
} from '../lib/appearance'
import { notify } from '../lib/notify'
import { THEMES, type ThemeId, useTheme } from '../lib/theme'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/appearance',
  component: AppearancePage,
})

const MAX_LOGO_BYTES = 256 * 1024 // 256 KB
const MAX_BG_BYTES = 2 * 1024 * 1024 // 2 MB

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

function AppearancePage() {
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const theme = useTheme((s) => s.theme)
  const setTheme = useTheme((s) => s.setTheme)
  const appearance = useAppearance()

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const onApplyTheme = (id: ThemeId) => {
    setTheme(id)
    const meta = THEMES.find((t) => t.id === id)
    notify.success(`Theme „${meta?.label}“ aktiviert`)
  }

  return (
    <SettingsLayout
      title="Design"
      description="Theme, Schriftart, Logo und Hintergrund. Alles lokal im Browser gespeichert — wirkt sofort."
    >
      <div className="space-y-4">
        <ThemeSection theme={theme} onApply={onApplyTheme} />
        <FontSection
          fontFamily={appearance.fontFamily}
          fontScale={appearance.fontScale}
          onFamily={appearance.setFontFamily}
          onScale={appearance.setFontScale}
        />
        <LogoSection
          logo={appearance.logo}
          customLogoDataUrl={appearance.customLogoDataUrl}
          brandLabel={appearance.brandLabel}
          onLogo={appearance.setLogo}
          onUpload={appearance.setCustomLogo}
          onBrandLabel={appearance.setBrandLabel}
        />
        <BackgroundSection
          background={appearance.background}
          customBackgroundDataUrl={appearance.customBackgroundDataUrl}
          onBackground={appearance.setBackground}
          onUpload={appearance.setCustomBackground}
        />

        <div className="flex justify-end pt-6 border-t border-border-subtle">
          <Button
            variant="ghost"
            onClick={() => {
              appearance.reset()
              notify.success('Design-Einstellungen zurückgesetzt')
            }}
          >
            <RotateCcw className="h-4 w-4" /> Auf Standard zurücksetzen
          </Button>
        </div>
      </div>
    </SettingsLayout>
  )
}

// === Sections =================================================================

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-base font-medium text-text-primary">{title}</h2>
        {description && <p className="text-sm text-text-tertiary mt-0.5">{description}</p>}
      </header>
      {children}
    </section>
  )
}

function ThemeSection({ theme, onApply }: { theme: ThemeId; onApply: (id: ThemeId) => void }) {
  return (
    <Section title="Theme" description="Farb-Set für die gesamte Anwendung.">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {THEMES.map((t) => {
          const active = theme === t.id
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onApply(t.id)}
              className={cn(
                'group relative overflow-hidden rounded-2xl border text-left transition-all',
                active
                  ? 'border-accent shadow-[0_0_0_1px_rgb(var(--c-accent)/0.4),0_0_24px_rgb(var(--c-accent)/0.15)]'
                  : 'border-border-subtle hover:border-border-default',
              )}
              aria-pressed={active}
            >
              <ThemePreview theme={t} />
              <div className="flex items-baseline justify-between gap-3 px-5 py-4 bg-bg-elevated/60 backdrop-blur-sm border-t border-border-subtle">
                <div>
                  <p className="font-medium text-text-primary flex items-center gap-2">
                    {t.label}
                    {active && <Check className="h-3.5 w-3.5 text-accent" strokeWidth={3} />}
                  </p>
                  <p className="text-xs text-text-tertiary mt-0.5">{t.description}</p>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </Section>
  )
}

function ThemePreview({ theme }: { theme: (typeof THEMES)[number] }) {
  const { bg, surface, text, accent } = theme.preview
  return (
    <div
      className="relative h-44 px-5 py-4 overflow-hidden"
      style={{
        backgroundColor: bg,
        color: text,
        backgroundImage: `radial-gradient(ellipse 80% 50% at 50% -10%, ${accent}22, transparent 70%)`,
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: accent }} />
        <span className="font-serif text-sm" style={{ color: text }}>
          Ember
        </span>
      </div>
      <div
        className="rounded-md px-3 py-2 mb-2 inline-block"
        style={{ backgroundColor: surface, border: `1px solid ${accent}30` }}
      >
        <p className="font-mono tabular-nums text-lg font-light" style={{ color: text }}>
          02:34:18
        </p>
      </div>
      <div
        className="inline-block ml-2 rounded-md px-3 py-1.5 text-xs font-medium"
        style={{ backgroundColor: accent, color: bg }}
      >
        Start
      </div>
      <div className="absolute bottom-3 left-5 right-5 flex gap-1">
        {[0.95, 0.7, 0.45, 0.3].map((a) => (
          <span
            key={a}
            className="h-1.5 flex-1 rounded-full"
            style={{ backgroundColor: accent, opacity: a }}
          />
        ))}
      </div>
    </div>
  )
}

function FontSection({
  fontFamily,
  fontScale,
  onFamily,
  onScale,
}: {
  fontFamily: FontFamilyId
  fontScale: FontScaleId
  onFamily: (id: FontFamilyId) => void
  onScale: (id: FontScaleId) => void
}) {
  return (
    <Section
      title="Schrift"
      description="Schriftart und Skalierung — wirkt auf alle Texte (Überschriften, Fließtext, Tabellen)."
    >
      <div className="space-y-4">
        <div>
          <Label className="mb-2 block">Schriftart</Label>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {FONT_FAMILIES.map((f) => {
              const active = fontFamily === f.id
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => onFamily(f.id)}
                  className={cn(
                    'rounded-xl border text-left px-4 py-3 transition-all',
                    active
                      ? 'border-accent bg-accent/5'
                      : 'border-border-subtle hover:border-border-default bg-bg-elevated/40',
                  )}
                  aria-pressed={active}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs uppercase tracking-wider text-text-tertiary">
                      {f.label}
                    </span>
                    {active && <Check className="h-3.5 w-3.5 text-accent" strokeWidth={3} />}
                  </div>
                  <div
                    className="mt-1.5 text-2xl text-text-primary"
                    style={{ fontFamily: f.stack }}
                  >
                    {f.preview}
                  </div>
                  <div className="mt-1 text-xs text-text-secondary" style={{ fontFamily: f.stack }}>
                    The quick brown fox · 0123456789
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Schriftgröße</Label>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {FONT_SCALES.map((s) => {
              const active = fontScale === s.id
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onScale(s.id)}
                  className={cn(
                    'rounded-xl border text-left px-4 py-3 transition-all',
                    active
                      ? 'border-accent bg-accent/5'
                      : 'border-border-subtle hover:border-border-default bg-bg-elevated/40',
                  )}
                  aria-pressed={active}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-text-primary">{s.label}</span>
                    {active && <Check className="h-3.5 w-3.5 text-accent" strokeWidth={3} />}
                  </div>
                  <div className="mt-0.5 text-xs text-text-tertiary">{s.description}</div>
                  <div className="mt-2 inline-flex items-baseline gap-1.5">
                    <Type className="h-3 w-3 text-text-tertiary" />
                    <span
                      className="text-text-secondary tabular-nums"
                      style={{ fontSize: `${13 * s.factor}px` }}
                    >
                      Aa {(s.factor * 100).toFixed(0)}%
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </Section>
  )
}

function LogoSection({
  logo,
  customLogoDataUrl,
  brandLabel,
  onLogo,
  onUpload,
  onBrandLabel,
}: {
  logo: LogoId
  customLogoDataUrl: string | null
  brandLabel: string
  onLogo: (id: LogoId) => void
  onUpload: (dataUrl: string | null) => void
  onBrandLabel: (s: string) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      notify.error('Bitte eine Bilddatei auswählen.')
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      notify.error('Logo zu groß (max. 256 KB).')
      return
    }
    try {
      const dataUrl = await readFileAsDataUrl(file)
      onUpload(dataUrl)
      onLogo('custom')
      notify.success('Logo gesetzt')
    } catch {
      notify.error('Logo konnte nicht gelesen werden.')
    } finally {
      e.target.value = ''
    }
  }

  return (
    <Section title="Logo & Brand" description="Marken-Icon und Wortmarke in der Sidebar.">
      <div className="space-y-4">
        <div>
          <Label className="mb-2 block">Symbol</Label>
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {LOGO_PRESETS.map((preset) => {
              const active = logo === preset.id
              const isCustom = preset.id === 'custom'
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => {
                    if (isCustom && !customLogoDataUrl) {
                      fileRef.current?.click()
                    } else {
                      onLogo(preset.id)
                    }
                  }}
                  className={cn(
                    'rounded-xl border px-3 py-4 transition-all flex flex-col items-center gap-1.5',
                    active
                      ? 'border-accent bg-accent/5'
                      : 'border-border-subtle hover:border-border-default bg-bg-elevated/40',
                  )}
                  aria-pressed={active}
                  title={preset.label}
                >
                  <div className="h-8 w-8 flex items-center justify-center">
                    {isCustom && customLogoDataUrl ? (
                      <img src={customLogoDataUrl} alt="" className="h-7 w-7 object-contain" />
                    ) : isCustom ? (
                      <ImageIcon className="h-5 w-5 text-text-tertiary" strokeWidth={1.5} />
                    ) : (
                      <PresetIcon id={preset.id} />
                    )}
                  </div>
                  <span className="text-[11px] text-text-secondary">{preset.label}</span>
                  {active && <Check className="h-3 w-3 text-accent" strokeWidth={3} />}
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/svg+xml,image/jpeg,image/webp"
            onChange={handleFile}
            className="hidden"
          />
          <Button variant="secondary" onClick={() => fileRef.current?.click()}>
            <Upload className="h-4 w-4" /> Eigenes Logo hochladen
          </Button>
          {customLogoDataUrl && (
            <Button
              variant="ghost"
              onClick={() => {
                onUpload(null)
                if (logo === 'custom') onLogo('flame')
                notify.success('Eigenes Logo entfernt')
              }}
            >
              <X className="h-4 w-4" /> Eigenes Logo löschen
            </Button>
          )}
          <span className="text-xs text-text-tertiary">PNG / SVG / JPG, max. 256 KB</span>
        </div>

        <div className="max-w-md">
          <Label htmlFor="brand-label" className="mb-2 block">
            Wortmarke
          </Label>
          <Input
            id="brand-label"
            value={brandLabel}
            onChange={(e) => onBrandLabel(e.target.value || 'Ember')}
            placeholder="Ember"
            maxLength={40}
          />
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-elevated/40 p-4">
          <p className="text-xs uppercase tracking-wider text-text-tertiary mb-3">Vorschau</p>
          <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-lg border border-border-subtle bg-bg-base">
            <BrandLogo size={20} />
            <span className="brand-mark text-xl">{brandLabel}</span>
          </div>
        </div>
      </div>
    </Section>
  )
}

function PresetIcon({ id }: { id: LogoId }) {
  const className = 'h-5 w-5 text-accent'
  const strokeWidth = 1.75
  switch (id) {
    case 'flame':
      return <Flame className={className} strokeWidth={strokeWidth} />
    case 'clock':
      return <Clock className={className} strokeWidth={strokeWidth} />
    case 'bolt':
      return <Zap className={className} strokeWidth={strokeWidth} />
    case 'feather':
      return <Feather className={className} strokeWidth={strokeWidth} />
    case 'mountain':
      return <Mountain className={className} strokeWidth={strokeWidth} />
    default:
      return <ImageIcon className={className} strokeWidth={strokeWidth} />
  }
}

function BackgroundSection({
  background,
  customBackgroundDataUrl,
  onBackground,
  onUpload,
}: {
  background: BackgroundId
  customBackgroundDataUrl: string | null
  onBackground: (id: BackgroundId) => void
  onUpload: (dataUrl: string | null) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      notify.error('Bitte eine Bilddatei auswählen.')
      return
    }
    if (file.size > MAX_BG_BYTES) {
      notify.error('Bild zu groß (max. 2 MB).')
      return
    }
    try {
      const dataUrl = await readFileAsDataUrl(file)
      onUpload(dataUrl)
      onBackground('custom')
      notify.success('Hintergrund gesetzt')
    } catch {
      notify.error('Bild konnte nicht gelesen werden.')
    } finally {
      e.target.value = ''
    }
  }

  return (
    <Section
      title="Hintergrund"
      description="Stilebene hinter dem Inhalt — von komplett ruhig bis vibrant Mesh-Gradient."
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {BACKGROUNDS.map((b) => {
          const active = background === b.id
          const isCustom = b.id === 'custom'
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => {
                if (isCustom && !customBackgroundDataUrl) {
                  fileRef.current?.click()
                } else {
                  onBackground(b.id)
                }
              }}
              className={cn(
                'rounded-xl border overflow-hidden text-left transition-all',
                active
                  ? 'border-accent shadow-[0_0_0_1px_rgb(var(--c-accent)/0.4)]'
                  : 'border-border-subtle hover:border-border-default',
              )}
              aria-pressed={active}
            >
              <BackgroundPreview id={b.id} customDataUrl={customBackgroundDataUrl} />
              <div className="px-3 py-2 bg-bg-elevated/60 border-t border-border-subtle">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-text-primary">{b.label}</span>
                  {active && <Check className="h-3 w-3 text-accent" strokeWidth={3} />}
                </div>
                <p className="mt-0.5 text-[11px] text-text-tertiary leading-snug">
                  {b.description}
                </p>
              </div>
            </button>
          )
        })}
      </div>

      <div className="flex items-center gap-3 flex-wrap pt-3">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFile}
          className="hidden"
        />
        <Button variant="secondary" onClick={() => fileRef.current?.click()}>
          <Upload className="h-4 w-4" /> Eigenes Hintergrundbild
        </Button>
        {customBackgroundDataUrl && (
          <Button
            variant="ghost"
            onClick={() => {
              onUpload(null)
              if (background === 'custom') onBackground('glow')
              notify.success('Eigenes Hintergrundbild entfernt')
            }}
          >
            <X className="h-4 w-4" /> Eigenes Bild löschen
          </Button>
        )}
        <span className="text-xs text-text-tertiary">PNG / JPG / WebP, max. 2 MB</span>
      </div>
    </Section>
  )
}

function BackgroundPreview({
  id,
  customDataUrl,
}: {
  id: BackgroundId
  customDataUrl: string | null
}) {
  const baseStyle: React.CSSProperties = {
    backgroundColor: 'rgb(var(--c-bg-base))',
  }
  let bgImage = ''
  if (id === 'glow') {
    bgImage =
      'radial-gradient(ellipse 80% 50% at 50% -20%, rgb(var(--c-accent) / 0.18), transparent 70%), radial-gradient(ellipse 60% 40% at 80% 100%, rgb(var(--c-accent) / 0.08), transparent 70%)'
  } else if (id === 'mesh') {
    bgImage =
      'radial-gradient(circle at 15% 20%, rgb(var(--c-accent) / 0.3), transparent 45%), radial-gradient(circle at 85% 30%, rgb(var(--c-info) / 0.22), transparent 50%), radial-gradient(circle at 75% 90%, rgb(var(--c-success) / 0.18), transparent 50%)'
  } else if (id === 'noise') {
    bgImage =
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.08 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")"
  }
  if (id === 'custom') {
    if (customDataUrl) {
      return (
        <div
          className="h-24"
          style={{
            backgroundImage: `url("${customDataUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )
    }
    return (
      <div className="h-24 flex items-center justify-center bg-bg-overlay/40">
        <ImageIcon className="h-6 w-6 text-text-tertiary" strokeWidth={1.5} />
      </div>
    )
  }
  return <div className="h-24" style={{ ...baseStyle, backgroundImage: bgImage }} />
}
